import { computed, watch } from "vue";
import { useRouter } from "vue-router";

import { useBreadcrumbs } from "@/components/TBreadcrumbs/useBreadcrumbs";
import { useFlag } from "@/composables/useFlags/useFlag";
import {
  TITAN_REMOTE_CRUMBS_CHANGE_EVENT,
  isEmbedded,
} from "@/remote/contract";

/**
 * Replaces whatever Titan's trail starts with ("System", an MSP or customer name) when embedded:
 * in the shell the user is inside the Cybersecurity product, not at Titan's org root. Hard-coded
 * because `useBreadcrumbs` builds the rest of the trail from untranslated strings too.
 */
export const EMBEDDED_ROOT_CRUMB_LABEL = "Cybersecurity";

/** One crumb as the host consumes it. Matches what red-app publishes. */
type PublishedCrumb = {
  id: string;
  label: string;
  href?: string;
};

/**
 * Publishes Titan's breadcrumb trail to the host as DATA — the host draws it, in one component,
 * for every app it embeds. Standalone this is inert: `TUpperNav` renders `TBreadcrumbs` itself,
 * and the layouts already hide that whole header when embedded.
 */
export function useCrumbsPublisher(host: HTMLElement | null): void {
  // Also stops `useBreadcrumbs` running twice standalone, where TBreadcrumbs owns it.
  if (!isEmbedded() || !host) return;

  const router = useRouter();
  const { isFeatureAvailable: isDashboardAvailableCustomer } = useFlag(
    "DASHBOARD_OVERVIEW_CUSTOMER",
  );

  // `items`, not `visibleItems`: the host decides what to collapse, so it needs the full trail.
  const { items } = useBreadcrumbs(isDashboardAvailableCustomer.value);

  const crumbs = computed<PublishedCrumb[]>(() =>
    items.value.map((crumb, index) => ({
      // Titan's crumbs carry no id, so one is derived. Index-prefixed because a trail can
      // legitimately repeat a name, and the host uses this as a track-by key.
      id: `${index}-${crumb.url || crumb.name}`,
      // Label only — the href is left alone, so the crumb still navigates to the org root it
      // stands for. See EMBEDDED_ROOT_CRUMB_LABEL for why the name is dropped.
      label: index === 0 ? EMBEDDED_ROOT_CRUMB_LABEL : crumb.name,
      // `resolve().href` applies the router base the host passed to `setRouterBase`, so the href
      // is host-absolute (`/thq/customer/…`) and its router can navigate it unchanged. An empty
      // url means the page the user is already on — no href, and the host renders it as text.
      ...(crumb.url ? { href: router.resolve(crumb.url).href } : {}),
    })),
  );

  watch(
    crumbs,
    (detail) => {
      // Bubbles + composed so the host can listen on `document` rather than on this element,
      // which it mounts lazily. Fires with an empty trail too — that is how a header clears.
      host.dispatchEvent(
        new CustomEvent<PublishedCrumb[]>(TITAN_REMOTE_CRUMBS_CHANGE_EVENT, {
          detail,
          bubbles: true,
          composed: true,
        }),
      );
    },
    { immediate: true },
  );
}

export default useCrumbsPublisher;
